<template>
    <div>
        <form enctype="multipart/form-data" novalidate>
            <div
                class="az-drop-file"
                :style="{ height: height }"
                @drop.prevent="onDropFiles($event.dataTransfer.items)"
                @dragover.prevent=""
            >
                <input
                    id="azFileSelector"
                    type="file"
                    :multiple="multiple"
                    class="input-file"
                    :name="uploadFieldName"
                    :accept="accept"
                    @change="onSelectFiles($event.target.files)"
                />
                <div class="center">
                    <slot></slot>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import {mimeTypes} from '@/core/constants'
import {mapState} from 'vuex'

export default {
    name: 'EdlFileUpload',
    props: {
        urlPersonalizadaUpload:{
            type: String,
            default: null
        },
        accept: {
            type: String,
            default: '*'
        },
        repository: {
            type: String,
            required: true
        },
        thumbnail: {
            type: Boolean,
            default: false
        },
        height: {
            type: String,
            default: '200px'
        },
        multiple: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            uploadFieldName: 'file',
            retornosApiPersonalizada: []
        }
    },
    computed:{
        ...mapState(['file'])
    },
    methods: {
        calcFileSize(bytes, unit) {
            const k = 1024
            return parseFloat((bytes / Math.pow(k, unit)).toFixed(2))
        },
        createFormData(file) {
            const formData = new FormData()
            formData.append(this.uploadFieldName, file)
            formData.append('repository', this.repository)
            formData.append('thumbnail', this.thumbnail)
            return formData
        },
        createPayload(file) {
            return {
                formData: this.createFormData(file),
                filename: file.name
            }
        },
        getMaxSizeConfig() {
            const fileConfig = this.$store.state.loki.file
            const maxSize = fileConfig.maxSize.replace(/([A-Za-z]+)/g, '')
            const unit = ['B', 'KB', 'MB', 'GB', 'TB'].indexOf(fileConfig.maxSize.replace(/\d+/g, '').toUpperCase())
            return { maxSize, unit }
        },
        isFileBiggerThanExpected(file) {
            const { maxSize, unit } = this.getMaxSizeConfig()
            const fileSize = this.calcFileSize(file.size, unit)
            return fileSize > maxSize
        },
        onDropFiles(eventItems) {
            if (!Object.keys(eventItems).length) {
                return
            }
            const files = []
            const ignoredUploadFiles = []
            Object.keys(eventItems).forEach(key => {
                const item = eventItems[key]
                if (item.kind === 'file') {
                    const file = item.getAsFile()
                    const extensaoDesconecida = !item.type
                    if((this.accept === '*' || this.accept.includes(item.type)) && !extensaoDesconecida){
                        files.push(file)
                    }else{
                        ignoredUploadFiles.push(file)
                    }
                }
            })
            const qtdAceitos = files.length
            const qtdIgnorados = ignoredUploadFiles.length
            this.notificarArquivosNaoInseridos(qtdAceitos, qtdIgnorados)
            this.uploadFiles(files)
        },
        async onSelectFiles(fileList) {
            if (!fileList.length) {
                return
            }
  
            const files = []
            const ignoredUploadFiles = []
            const ignoredUploadFilesLock = []
            const filePromises = []
  
            Object.keys(fileList).forEach(key => {
                const item = fileList[key]
                const extensaoDesconecida = !item.type
  
                if ((this.accept === '*' || this.accept.includes(item.type)) && !extensaoDesconecida) {
                    files.push(item)
                } else {
                    ignoredUploadFiles.push(item)
                }
  
                const filePromise = new Promise(async (resolve) => {
                    const reader = new FileReader()
                    reader.readAsArrayBuffer(item)
  
                    reader.onload = async function () {
                        const filesToVerify = new Blob([reader.result], { type: 'application/pdf' })
                        await filesToVerify.text().then(x => {
                            if (x.includes('Encrypt') || x.substring(x.lastIndexOf('<<'), x.lastIndexOf('>>')).includes('/Encrypt')) {
                                ignoredUploadFilesLock.push(item)
                            }
  
                            resolve()
                        })
                    }
                })
  
                filePromises.push(filePromise)
            })
  
            await Promise.all(filePromises)
  
            const qtdAceitos = files.length
            const qtdIgnorados = ignoredUploadFiles.length
            const qtdIgnoradosLock = ignoredUploadFilesLock.length
            this.notificarArquivosNaoInseridos(qtdAceitos, qtdIgnorados, qtdIgnoradosLock)
  
            if (qtdIgnorados === 0 && qtdIgnoradosLock === 0) {
                this.uploadFiles(files)
                this.resetSelectedFiles()
            }
        } ,
        notificarArquivosNaoInseridos(qtdAceitos, qtdIgnorados, qtdIgnoradosLock){
            if(qtdIgnorados > 0 || qtdIgnoradosLock > 0) {
                let {extensoesValidas, multiplasExtensoes} = this.converterAcceptEmTextoLegivel(this.accept)
                let mensagemCompleta = ''
                if (qtdAceitos === 0) {
                    if(qtdIgnorados === 1){
                        mensagemCompleta += 'O arquivo não foi anexado. '
                    }else {
                        mensagemCompleta += 'Nenhum arquivo foi anexado. '
                    }
                } else {
                    if (qtdIgnorados === 1 || qtdIgnoradosLock === 1) {
                        mensagemCompleta += 'Um arquivo não foi anexado. '
                    } else {
                        mensagemCompleta += 'Alguns arquivos não foram anexados. '
                    }
                }
  
                if (qtdIgnoradosLock > 0) {
                    mensagemCompleta += `O formato ${extensoesValidas} é aceito apenas sem senha.`
                } else if(this.accept !== '*') {
                    mensagemCompleta += `Apenas ${multiplasExtensoes ? `os formatos ${extensoesValidas} são aceitos.` : `o formato ${extensoesValidas} é aceito.`}`
                }else{
                    mensagemCompleta += 'Só serão aceitos arquivos com extensão. Exemplo: arquivo.pdf, arquivo.docx e etc...'
                }
                this.mostrarNotificacaoErro(mensagemCompleta)
            }
        },
        converterAcceptEmTextoLegivel(accept){
            const extensoesAccept = accept.replace(/\s+/g, '').split(',')
            let extensoesArray = Object.values(mimeTypes).filter(mime => extensoesAccept.some(ext => mime.valor.includes(ext))).map(mime => mime.extensao)
            const extensoesValidas = [extensoesArray.slice(0, -1).join(', '), extensoesArray.slice(-1)[0]].join(extensoesArray.length < 2 ? '' : ' e ')
            return {
                extensoesValidas: extensoesValidas,
                multiplasExtensoes: extensoesAccept.length > 1
            }
        },
        openFileSelector() {
            document.getElementById('azFileSelector').click()
        },
        uploadFiles(fileList) {
            let apiDefault0 = this.file
            if(this.urlPersonalizadaUpload){
                const apiCustom = {
                    api: this.urlPersonalizadaUpload,
                    maxSize: '30Mb'
                }
                this.setApiArquivos(apiCustom)
            }
            Array.from(Array(fileList.length).keys()).forEach(async (x, idx, array) => {
                const file = fileList[x]
                if (this.isFileBiggerThanExpected(file)) {
                    this.throwFileExceedMaxLimitSizeEvent(file)
                } else {
                    const payload = this.createPayload(file)
                    if(this.urlPersonalizadaUpload){
                        const retorno = await this.uploadFile(payload)
                        this.retornosApiPersonalizada.push(retorno)
                    }else{
                        await this.uploadFile(payload)
                    }
                }
                if(idx === array.length - 1){
                    this.$emit('uploadCompleto')
                    this.setApiArquivos(apiDefault0)
                }
            })
        },
        resetSelectedFiles() {
            document.getElementById('azFileSelector').value = ''
        },
        throwFileExceedMaxLimitSizeEvent(file) {
            this.mostrarNotificacaoErro('O arquivo ' + file.name + ' ultrapassa o tamanho máximo permitido de 30MB.')
        }
    }
}
</script>

<style scoped lang="stylus">
.az-drop-file
  border: 2px dashed #ccc
  margin: 10px 0
  align-content: center
  align-items: center
  text-align: center
  background-color: #eeeeee
  color: #7b7b7b
  display: flex
  position: relative

  &:hover
    border-color: #939393

  .center
    margin: 0 auto
    padding: 0

  .input-file
    opacity: 0
    position: absolute
    left: 0
    top: 0
    width: 10px
    height: 10px
    z-index: 1

  &__big
    padding: 70px 0 60px !important

  p
    width: 100%
    color: #777
    font-size: 13px
    margin-bottom: 0
    margin-top: 15px

  a
    background-color: #d28a2C
    color: white
    padding: 10px 15px 10px 10px
    border-radius: 2px
    font-size: 13px
    &:hover
        background-color: lighten(#d28a2C, 10%)
        border: 1px solid lighten(#d28a2C, 10%) !important

      i
        color: white !important
        position: relative
        top: 2px
        font-size: 18px
        margin-right: 5px
</style>
